import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { ReportHeader, SummaryCards, FilterPanel, ExportButtons } from '../../components/reports';
import { D3BarChart, D3PieChart } from '../../components/charts';
import { getTopDebtors } from '../../services/project750kService';
import { exportToExcel, exportToPDF } from '../../utils/exportUtils';

const PageContainer = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TableContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
    font-family: 'Segoe UI', sans-serif;
  }

  th {
    background-color: #0077B5;
    color: white;
    font-weight: 600;
  }

  tbody tr {
    cursor: pointer;
    
    &:hover {
      background-color: #e3f2fd;
    }
  }
`;

const RankBadge = styled.div`
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  font-size: 18px;
  color: #0077B5;
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const InfoBanner = styled.div`
  background-color: #e3f2fd;
  color: #1565c0;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-family: 'Segoe UI', sans-serif;
  font-size: 14px;
`;

const TopDebtors = () => {
  const [debtors, setDebtors] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(20);
  const [minFilings, setMinFilings] = useState(2);
  // Temporary state for slider value (updates UI immediately, but doesn't trigger API call)
  const [tempMinFilings, setTempMinFilings] = useState(2);

  // Sync tempMinFilings when minFilings changes externally
  useEffect(() => {
    setTempMinFilings(minFilings);
  }, [minFilings]);

  useEffect(() => {
    loadData();
  }, [limit, minFilings]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getTopDebtors(limit, minFilings);
      setDebtors(result.debtors || []);
      setChartData(result.chartData || []);
      setSummary(result.summary || {});
      setMeta(result.meta || null);
    } catch (err) {
      setError(`Error loading top debtors: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = useCallback(async () => {
    try {
      await exportToExcel(debtors, 'Top_Debtors', 'Debtors', { limit, minFilings });
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    }
  }, [debtors, limit, minFilings]);

  const handleExportPDF = useCallback(async () => {
    try {
      await exportToPDF({
        title: 'Top Debtors Report',
        data: debtors.slice(0, 100),
        chartElementId: 'bar-chart',
        summary: {
          'Total Debtors': summary?.totalDebtors || 0,
          'Multi-Filers': summary?.multiFilersPercent + '%' || '0%',
          'Top Debtor': summary?.topDebtor || 'N/A',
          'Top Debtor Filings': summary?.topDebtorFilings || 0
        },
        filename: 'Top_Debtors',
        filters: { limit, minFilings }
      });
    } catch (err) {
      setError(`PDF export failed: ${err.message}`);
    }
  }, [debtors, summary, limit, minFilings]);

  // Handle slider change - update temporary value for immediate UI feedback
  const handleSliderChange = (value) => {
    setTempMinFilings(value);
  };

  // Handle slider mouse up - update actual filter value to trigger API call
  const handleSliderMouseUp = () => {
    setMinFilings(tempMinFilings);
  };

  const filterConfig = [
    {
      type: 'range',
      label: 'Min Filings',
      value: tempMinFilings,
      onChange: handleSliderChange,
      onMouseUp: handleSliderMouseUp,
      min: 1,
      max: 20
    }
  ];

  const summaryCards = summary ? [
    { label: 'Total Debtors', value: summary.totalDebtors },
    { label: 'Top Debtor', value: summary.topDebtor },
    { label: 'Top Debtor Filings', value: summary.topDebtorFilings },
    { label: 'Multi-Filers', value: summary.multiFilersPercent + '%' }
  ] : [];

  // Prepare bar chart data
  const barChartData = chartData.map(d => ({
    label: d.name.length > 30 ? d.name.substring(0, 30) + '...' : d.name,
    value: d.filings
  }));

  // Prepare pie chart data (Single-filer vs Multi-filer)
  const pieChartData = summary ? [
    { label: 'Multi-Filers', value: parseInt(summary.multiFilersPercent) || 0 },
    { label: 'Single-Filers', value: 100 - (parseInt(summary.multiFilersPercent) || 0) }
  ] : [];

  if (loading) return <LoadingSpinner>Loading top debtors...</LoadingSpinner>;

  return (
    <PageContainer>
      <ReportHeader icon="🏆" title="Top Debtors" />

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {meta && (
        <InfoBanner>
          ℹ️ Rankings based on <strong>{meta.recordsScanned?.toLocaleString() || 0} records scanned</strong>
          {meta.hasMore && ' (representative sample)'}
        </InfoBanner>
      )}

      <FilterPanel filters={filterConfig} />
      
      <SummaryCards cards={summaryCards} />
      
      <ExportButtons
        onExportExcel={handleExportExcel}
        onExportPDF={handleExportPDF}
        loading={loading}
      />

      <ChartsRow>
        <ChartSection id="bar-chart">
          <h3 style={{ fontFamily: 'Segoe UI', marginBottom: '20px' }}>Top 20 Debtors by Filings</h3>
          <D3BarChart
            data={barChartData}
            xKey="label"
            yKey="value"
            yLabel="Filing Count"
            horizontal={true}
            height="600px"
          />
        </ChartSection>

        <ChartSection id="pie-chart">
          <h3 style={{ fontFamily: 'Segoe UI', marginBottom: '20px' }}>Filer Distribution</h3>
          <D3PieChart
            data={pieChartData}
            labelKey="label"
            valueKey="value"
            height="600px"
          />
        </ChartSection>
      </ChartsRow>

      <TableContainer>
        <h3 style={{ fontFamily: 'Segoe UI', marginBottom: '20px' }}>
          Leaderboard ({debtors.length} debtors with {minFilings}+ filings)
        </h3>
        <Table>
          <thead>
            <tr>
              <th>Rank</th>
              <th>Debtor Name</th>
              <th>Debtor ID</th>
              <th>Total Filings</th>
            </tr>
          </thead>
          <tbody>
            {debtors.map((debtor, index) => (
              <tr key={index}>
                <td>
                  <RankBadge>{index + 1}</RankBadge>
                </td>
                <td><strong>{debtor.name}</strong></td>
                <td>{debtor.debtorId || 'N/A'}</td>
                <td><strong>{debtor.filings.toLocaleString()}</strong></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    </PageContainer>
  );
};

export default TopDebtors;

